import useServiceById from "../../../../hooks/useServiceById";
import cleanUpAppId from "../../../../utils/cleanUpAppId";
import { Text, Tooltip, TooltipFocusableTrigger } from "@zapier/design-system";
import { ServiceIconWithFallback } from "../../../ServiceIconWithFallback";
import getAppIconUrl from "../../../../utils/getAppIconUrl";
import { Node, NodeProps } from "@xyflow/react";
import {
  areHandlesVisibleAtomFamily,
  hasSourceNodeAtomFamily,
  hasVisibleAnnotationAtomFamily,
  isNodeHoveredAtomFamily,
  isPathStep,
  shouldShowAddStepButtonAtom,
  shouldShowStepIdsAtom,
  ZapStepNodeData,
  isDraftNotTemplateNodeAtomFamily,
} from "@zapier/canvas-state";
import { ZAP_STEP_NODE_HANDLE_DIST_FROM_TOP } from "@zapier/canvas-constants";
import classNames from "classnames";
import { useAtomValue } from "jotai";

import { StepId } from "../../../StepId";
import getZapStepTitle from "../../../../utils/getZapStepTitle";
import isFilterStep from "../../../../utils/isFilterStep";

import { AddStepButton } from "../../../AddStepButton";
import { Handles } from "../../../Handles";
import { NodeHoverWrapper } from "../../../NodeHoverWrapper";
import styles from "./ZapStepNode.module.css";

export function ZapStepNode({
  data,
  id,
  parentId,
}: NodeProps<Node<ZapStepNodeData>>) {
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const { stepNumber, step, isZapPublished } = data;
  const getService = useServiceById();
  const appId = step.app ?? "";
  const service = getService(appId);
  const images = service?.images;
  const serviceName = service?.name ?? cleanUpAppId(appId);
  const isPath = isPathStep(step);
  const shouldShowAddStepButton = useAtomValue(shouldShowAddStepButtonAtom);
  const hasSourceNode = useAtomValue(hasSourceNodeAtomFamily(id));

  const isParenDraftNodeNotTemplate = useAtomValue(
    isDraftNotTemplateNodeAtomFamily(parentId),
  );

  const areParentHandlesVisible = useAtomValue(
    areHandlesVisibleAtomFamily(parentId),
  );
  const isParentHovered = useAtomValue(isNodeHoveredAtomFamily(parentId));
  const hasVisibleAnnotationOnParent = useAtomValue(
    hasVisibleAnnotationAtomFamily(parentId!),
  );

  const size = "x-small";
  const stepTitle = getZapStepTitle({
    stepNumber,
    step,
    isPath,
    serviceName,
  });

  const isFilter = isFilterStep(step);
  const areHandlesVisible = useAtomValue(areHandlesVisibleAtomFamily(id));

  return (
    <>
      {shouldShowStepIds && <StepId />}
      {isZapPublished && !hasSourceNode && shouldShowAddStepButton && (
        <AddStepButton
          handleType="target"
          distanceFromTop={ZAP_STEP_NODE_HANDLE_DIST_FROM_TOP}
        />
      )}
      {isZapPublished && shouldShowAddStepButton && (
        <AddStepButton
          handleType="source"
          distanceFromTop={ZAP_STEP_NODE_HANDLE_DIST_FROM_TOP}
        />
      )}
      <NodeHoverWrapper>
        <div
          className={classNames(styles.step, {
            [styles.pathStep]: isPath,
            [styles.filterStep]: isFilter,
            [styles.isParentHovered]: isParentHovered,
            [styles.wrapperSingleSelected]: hasVisibleAnnotationOnParent,
            [styles.isGhostNode]: isParenDraftNodeNotTemplate,
          })}
        >
          <Handles
            nodeType="zapStep"
            isVisible={
              isZapPublished && (areParentHandlesVisible || areHandlesVisible)
            }
          />
          <Tooltip placement="top" content={serviceName}>
            <TooltipFocusableTrigger>
              <ServiceIconWithFallback
                hasBorder={true}
                serviceName={serviceName}
                size={size}
                src={getAppIconUrl(appId, images, size)}
              />
            </TooltipFocusableTrigger>
          </Tooltip>
          <Tooltip
            placement="top"
            content={`${stepNumber}. ${stepTitle}`}
            allowMultiline
          >
            <TooltipFocusableTrigger className={styles.truncate}>
              <Text
                type="SmallPrint2"
                color={isPath ? "PrimeWhite" : "GrayWarm9"}
              >
                {`${stepNumber}. ${stepTitle}`}
              </Text>
            </TooltipFocusableTrigger>
          </Tooltip>
        </div>
      </NodeHoverWrapper>
    </>
  );
}
